import React, { MouseEventHandler, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

type ModalShellProps = {
    title: string;
    subtitle?: string;
    onClose: MouseEventHandler | (() => void);
    children: React.ReactNode;
    footer?: React.ReactNode;
    /** Tailwind max-width class for the modal card. Defaults to a standard dialog width. */
    maxWidthClass?: string;
}

const ModalShell = ({ title, subtitle, onClose, children, footer, maxWidthClass = "max-w-2xl" }: ModalShellProps) => {
    const { t } = useTranslation();
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Remember what had focus before the modal opened, so we can restore it on close.
        const previouslyFocused = document.activeElement as HTMLElement | null;

        const firstInput = contentRef.current?.querySelector<HTMLElement>('input, select, textarea');
        if (firstInput) {
            firstInput.focus();
        } else {
            const firstButton = contentRef.current?.querySelector<HTMLElement>('button:not([data-modal-close])');
            firstButton?.focus();
        }

        const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

        const handleKeyDown = (e: KeyboardEvent) => {
            // Close on Escape, mirroring the overlay/X buttons.
            if (e.key === 'Escape') {
                e.stopPropagation();
                (onClose as () => void)();
                return;
            }
            // Trap Tab focus inside the modal so the close (X) and all controls stay reachable
            // and focus can't leak to the page behind the overlay.
            if (e.key === 'Tab' && contentRef.current) {
                const focusables = Array.from(contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                const active = document.activeElement;
                const inside = contentRef.current.contains(active);
                if (e.shiftKey && (active === first || !inside)) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && (active === last || !inside)) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            // Return focus to the trigger so keyboard users don't lose their place.
            previouslyFocused?.focus?.();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="fixed inset-0 z-50">
            <div
                className="fixed inset-0 bg-dark-gray opacity-80 transition-colors duration-300"
                onClick={onClose}
            />
            <div className="fixed inset-0 z-10 flex items-center justify-center p-4 overflow-y-auto">
                <div className={`flex flex-col bg-white w-full ${maxWidthClass} sm:min-w-[600px] shadow rounded-lg transition-colors duration-300`} ref={contentRef}>
                    <div className="flex justify-end p-3">
                        <button
                            type="button"
                            onClick={onClose}
                            data-modal-close
                            className="text-secondary-gray bg-transparent hover:bg-gray-lines hover:text-off-black rounded-lg text-sm p-1.5 inline-flex items-center transition-colors duration-300"
                        >
                            <svg aria-hidden="true" className="w-5 h-5 text-secondary-gray transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            <span className="sr-only">{t("Close Modal")}</span>
                        </button>
                    </div>
                    <div className="px-6 pb-6 lg:px-8">
                        <h2 className="text-2xl font-semibold text-off-black">
                            {title}
                            {subtitle && <span className="text-secondary-gray"> — {subtitle}</span>}
                        </h2>
                        <div className="mt-5">
                            {children}
                        </div>
                        {footer && (
                            <div className="flex flex-row-reverse gap-4 mt-10">
                                {footer}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalShell;
